import { Alert } from '@linktr.ee/arbor/components/Alert';
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

enum IdentifierErrorCodes {
  IdentifierIsEmpty = 'identifier_required',
  IdentifierErrorEmailIsIncorrect = 'invalid_email',
  IdentifierErrorUsernameIsIncorrect = 'invalid_username',
}

enum PasswordErrorCodes {
  PasswordIsInvalid = 'invalid_password',
}

export interface LoginErrorProps {
  identifierError?: string;
  passwordError?: string;
  pageError?: {
    error?: string;
    description?: string;
    errorDescription?: string;
  };
  failCount?: number;
  emailErrorCount?: number;
  usernameErrorCount?: number;
  onTriggerOtpFlow?: () => void;
  handleSignUp?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

export const LoginError = memo(
  ({
    identifierError,
    passwordError,
    pageError,
    failCount = 0,
    emailErrorCount = 0,
    usernameErrorCount = 0,
    onTriggerOtpFlow,
    handleSignUp,
  }: LoginErrorProps) => {
    const { t } = useTranslation();

    if (
      identifierError === undefined &&
      passwordError === undefined &&
      pageError === undefined
    ) {
      return null;
    }

    const renderSupportLink = (linkText: string): ReactElement => (
      <a
        href={`${LINKTREE_BASE_URL}/support`}
        className="text-body-small-medium text-accent underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkText}
      </a>
    );

    let errorMessageHeader: string | null = null;
    let errorMessageBody: string | ReactElement | null = null;

    if (pageError !== undefined) {
      if (failCount > 2) {
        errorMessageHeader = t('Having trouble logging in?');
        errorMessageBody = renderSupportLink(t('Contact support'));
      } else {
        errorMessageHeader = t('An error occurred');
        errorMessageBody =
          pageError.error ||
          pageError.description ||
          pageError.errorDescription ||
          t('Please try again.');
      }
    }

    if (identifierError !== undefined) {
      switch (identifierError) {
        case IdentifierErrorCodes.IdentifierIsEmpty:
          errorMessageHeader = t('Please enter an email or username.');
          errorMessageBody = (
            <a
              href={`${LINKTREE_BASE_URL}/forgot-username`}
              className="text-body-small-medium text-accent underline"
            >
              {t('Forgot username?')}
            </a>
          );
          break;
        case IdentifierErrorCodes.IdentifierErrorEmailIsIncorrect:
          errorMessageHeader = t("Let's double-check your email address.");
          if (emailErrorCount === 1) {
            errorMessageBody = t('Or, try logging in with your username.');
          } else if (emailErrorCount === 2) {
            errorMessageBody = (
              <>
                {t("Don't have an account?")}{' '}
                <button
                  type="button"
                  className="text-body-small-medium text-accent underline"
                  onClick={handleSignUp}
                >
                  {t('Sign up')}
                </button>
              </>
            );
          } else {
            errorMessageBody = renderSupportLink(t("Still can't log in?"));
          }
          break;
        case IdentifierErrorCodes.IdentifierErrorUsernameIsIncorrect:
          errorMessageHeader = t("Let's double-check your username.");

          if (usernameErrorCount === 1) {
            errorMessageBody = (
              <a
                href={`${LINKTREE_BASE_URL}/forgot-username`}
                className="text-body-small-medium text-accent underline"
              >
                {t('Find your username')}
              </a>
            );
          } else if (usernameErrorCount === 2) {
            errorMessageBody = t('Or, try logging in with your email.');
          } else if (usernameErrorCount === 3) {
            errorMessageBody = (
              <>
                {t("Don't have an account?")}{' '}
                <button
                  type="button"
                  className="text-body-small-medium text-accent underline"
                  onClick={handleSignUp}
                >
                  {t('Sign up')}
                </button>
              </>
            );
          } else {
            errorMessageBody = renderSupportLink(t("Still can't log in?"));
          }
          break;
        default:
          errorMessageHeader = t("Let's double-check your details");
          errorMessageBody = t('Or, try logging in with your username.');
          break;
      }
    }

    if (passwordError !== undefined) {
      switch (passwordError) {
        case PasswordErrorCodes.PasswordIsInvalid:
          errorMessageHeader = t('Incorrect password');
          if (failCount >= 3) {
            errorMessageBody = (
              <a
                href={`${LINKTREE_BASE_URL}/request`}
                className="text-body-small-medium text-accent underline"
              >
                {t('Forgot password?')}
              </a>
            );
          } else {
            errorMessageBody = onTriggerOtpFlow ? (
              <button
                type="button"
                className="text-body-small-medium text-accent underline"
                onClick={onTriggerOtpFlow}
              >
                {t('Get a one-time code')}
              </button>
            ) : (
              t('Please try again.')
            );
          }
          break;
        default:
          errorMessageHeader = t("Let's double-check your password.");
          break;
      }
    }

    // Show generic error if we have high fail counts
    if (
      !errorMessageHeader &&
      !errorMessageBody &&
      (failCount >= 3 || emailErrorCount >= 3 || usernameErrorCount >= 3)
    ) {
      errorMessageHeader = t('Multiple login attempts failed.');
      errorMessageBody = onTriggerOtpFlow ? (
        <button
          type="button"
          className="text-body-small-medium text-accent underline"
          onClick={onTriggerOtpFlow}
        >
          {t('Try logging in with email verification instead')}
        </button>
      ) : (
        renderSupportLink(t('Contact support'))
      );
    }

    if (!errorMessageHeader && !errorMessageBody) {
      return null;
    }

    return (
      <div className="mb-4">
        <Alert
          status="error"
          variant="page-level"
          title={errorMessageHeader || ''}
          message={errorMessageBody || ''}
        />
      </div>
    );
  }
);

LoginError.displayName = 'LoginError';

export default LoginError;
